/**
 * Image Studio contract types.
 *
 * DTO properties are **PascalCase** — these mirror the ASP.NET Core wire contract
 * agreed in docs/planning/images/2026-08-12-WEB-position-and-backend-brief.md.
 * Local variables and methods stay camelCase per .conventions.md.
 *
 * The endpoints are LIVE — `ImageStudioService` calls them for real (`useMock = false`;
 * the mock survives only for the specs and for working offline). These interfaces are
 * the client half of the contract implemented by ArtFlow.API's ImageAssetController,
 * ImageRecipeController and ImageGenerateController; when they disagree, the API wins.
 */

/** What produced an asset. `Original` is never altered and never deleted. */
export type ImageAssetKind = 'Original' | 'Corrected' | 'Generated' | 'Derivative';

/**
 * Generated assets arrive `Provisional` and only become `Accepted` when the artist
 * says so — that accept/decline step is the consent moment for AI reinterpreting
 * the work, and nothing provisional may become the product's image.
 */
export type ImageAssetStatus = 'Provisional' | 'Accepted' | 'Declined';

export type JobStatus = 'Queued' | 'Running' | 'Succeeded' | 'Failed';

/** The two outcomes the interface offers. Provider choice is the SERVER's, not ours. */
export type GenerateOperation = 'Catalogue' | 'Scene';

export interface ImageAsset {
  Id: string;
  ArtistProductId: string;
  Kind: ImageAssetKind;
  /**
   * The artist's decision about THIS asset. It is NOT "is this the artwork's image" —
   * that is `IsCurrent`. Originals and deterministic corrections arrive `Accepted`
   * because they need no consent step, so several assets are `Accepted` at once and
   * reading "in use" from this shows the badge on all of them.
   */
  Status: ImageAssetStatus;
  /** The single pointer to what the artwork actually shows. Exactly one per artwork. */
  IsCurrent: boolean;
  Url: string;
  Width: number;
  Height: number;
  CreatedDate: string;
  /**
   * What this was derived from. Null only for `Original`.
   *
   * Note that a render's derivatives point at the ORIGINAL, not at the master they were
   * resized from — they are siblings of the `Corrected` asset, not its children.
   */
  SourceAssetId: string | null;
  /** `master` | `catalogue` | `web` | `thumb` on a `Derivative`; null otherwise. */
  DerivativeName: string | null;
  /** Which generative operation produced this: `catalogue` | `scene`. Null otherwise. */
  Operation: string | null;
  /** Populated for `Generated` only — provenance, so a result can be explained later. */
  Provider: string | null;
  PromptId: string | null;
  /**
   * What this version is, in the artist's own words — the answer to "why did I change this?"
   * read six months later.
   *
   * Arrives already filled with a factual default derived from the recipe or the provenance
   * ("Brighter, warmer and straightened 1.4°", "Catalogue image — Google"), because an empty
   * box written for a reader who does not exist yet stays empty. The artist overwrites it with
   * the half that cannot be derived — why, not what.
   *
   * PRIVATE. A memo to the artist, never rendered publicly.
   */
  Label: string | null;
}

/**
 * Adjustments are normalised to [-1, 1] with 0 = no-op, EXCEPT `Denoise` and
 * `Sharpen.Amount` which are [0, 1]. The client renders an approximate preview from
 * these; the SERVER's render from the original is the authoritative result.
 */
export interface RecipeAdjustments {
  Exposure: number;
  Brightness: number;
  Contrast: number;
  Highlights: number;
  Shadows: number;
  Temperature: number;
  Tint: number;
  Saturation: number;
  Vibrance: number;
  Denoise: number;
  Sharpen: { Amount: number; Radius: number; Threshold: number };
}

/**
 * The four corners of a flat work within the photograph, as fractions of the source
 * (0–1, origin top-left), clockwise from top-left.
 *
 * This is the FAITHFUL correction for a painting or print: the server samples the
 * artist's own pixels at any ratio, at full resolution, for nothing. Generation cannot
 * do this — `gpt-image-1` offers only 1:1, 3:2 and 2:3, and redraws rather than
 * transforms, so a 5×7 work comes back re-composed at a shape it supports.
 *
 * Out-of-range values are clamped by the server rather than rejected; a degenerate or
 * self-crossing selection is ignored silently and costs the artist nothing.
 */
export interface RecipePerspective {
  TopLeft: { X: number; Y: number };
  TopRight: { X: number; Y: number };
  BottomRight: { X: number; Y: number };
  BottomLeft: { X: number; Y: number };
  /**
   * Width ÷ height. Always send it: the server can derive a ratio from the corners,
   * but that recovers the PROJECTED shape, and the error grows with the angle of the
   * shot — worst exactly where rectification matters. The artist's eye is better.
   */
  TargetAspect: number | null;
}

/**
 * The part of the source that survives, as FRACTIONS of the source (0–1, origin top-left) —
 * the same convention as `Geometry.Perspective`, and for the same reason: one recipe has to be
 * valid for both the preview proxy and the full-resolution original.
 *
 * **This can only ever remove.** The server renders it with `ExtractArea`, which is incapable of
 * adding canvas, so a crop cannot invent pixels the camera never saw. Making the work *smaller*
 * within a frame is a different operation entirely (it adds space) and lives in `Presentation`.
 *
 * The never-crop rule is not violated by this: it forbids cropping the artist's work **without
 * being told to**. Here the artist is doing the telling, and unlike a crop baked in at import this
 * one is undoable — the `Original` is untouched and the recipe is stored, so the framing can be
 * changed or removed for as long as the artwork exists.
 */
export interface RecipeCrop {
  X: number;
  Y: number;
  Width: number;
  Height: number;
}

/**
 * The work placed SMALLER inside a frame of the same size, with ground around it — the exact
 * counterpart to `RecipeCrop`, which can only ever remove.
 *
 * One scale drives both axes, so this cannot stretch, and the server builds it with an embed,
 * which cannot crop. The artist trades resolution in the work for space around it, and neither
 * the shape nor the edges of the work can be lost in the process.
 */
export interface RecipeFrame {
  /**
   * [0.2, 1]. 1 fills the frame; below that the work shrinks and ground appears.
   *
   * **Send `Frame` or `Crop`, never both** — they are the two directions of one control, and the
   * server would dutifully apply both. The studio clears one whenever it sets the other.
   */
  Scale: number;
  /**
   * Offset from centre in fractions of the CANVAS. 0 is centred, ±0.5 pushes the work half out.
   *
   * Against the canvas rather than against the leftover space, so the work travels the same
   * distance per pixel of drag at every scale — and can be moved at all at scale 1, where there is
   * no leftover space to move through. The cost is that a large offset pushes part of the work out
   * of frame and it is not kept; that is the artist choosing to crop, clamped so it cannot happen
   * by accident.
   */
  X: number;
  Y: number;
  /** Hex ground. Omitted means white. */
  Background?: string | null;
}

export interface EditRecipe {
  SchemaVersion: string;
  SourceAssetId: string;
  /** Fractions of the source, never pixels — so one recipe fits preview and original. */
  Geometry: {
    Rotate: number;
    Perspective?: RecipePerspective | null;
    Crop?: RecipeCrop | null;
    Frame?: RecipeFrame | null;
  };
  Adjustments: RecipeAdjustments;
  /** Catalogue framing by reference, so changing the look doesn't re-emit stored recipes. */
  Presentation: { PresetId: string } | null;
  Output: { ColourSpace: string; Derivatives: string[] };
}

export interface RenderJob {
  JobId: string;
  Status: JobStatus;
  ResultAssetId: string | null;
  Error: string | null;
}

/** One metering window. Both are enforced; whichever runs out first blocks. */
export interface ImageQuotaWindow {
  Used: number;
  Limit: number;
  Remaining: number;
}

/**
 * Read before the artist commits, so nothing fails after the fact.
 *
 * Two windows, not one: a daily cap paces usage, a monthly cap bounds spend. An artist
 * with allowance left today can still be out for the month, so the UI has to show both
 * — "2 left today" while the month is exhausted would be a lie.
 */
export interface ImageQuota {
  Daily: ImageQuotaWindow;
  Monthly: ImageQuotaWindow;
  /** `OwnKey` = the artist supplied their own provider credentials; we don't meter them. */
  Source: 'Plan' | 'OwnKey';
}

/**
 * ⚠️ POC ONLY — HARDCODED, AND THIS IS NOT WHERE THEY BELONG.
 *
 * These limits must move into the **SubscriptionRuleSet**, administered at
 * `/subscription-rules`, alongside the existing "Number Of Allowed AI-Based
 * Descriptions" limit which is the direct precedent for a per-plan AI meter.
 *
 * Two things to carry across when they move:
 *  - that page states new limits apply **only to new signups**, so existing artists
 *    need a deliberate answer before image quotas mean anything to them;
 *  - the existing AI meter is monthly only. This one needs BOTH a daily and a monthly
 *    field, so it is a schema addition rather than a new row in an existing shape.
 *
 * Rules as specified, non-negotiable for this POC:
 *  - every generation is charged, INCLUDING ones the artist declines;
 *  - own-key users are unmetered, subject to server load.
 */
export const POC_IMAGE_LIMITS = {
  Free: { PerDay: 2, PerMonth: 10 },
  Paid: { PerDay: 4, PerMonth: 20 },
} as const;

/** Builds a fresh, unused quota for a plan. POC helper — the server owns this later. */
export function pocQuotaFor(plan: 'Free' | 'Paid'): ImageQuota {
  const limits = POC_IMAGE_LIMITS[plan];
  return {
    Daily: { Used: 0, Limit: limits.PerDay, Remaining: limits.PerDay },
    Monthly: { Used: 0, Limit: limits.PerMonth, Remaining: limits.PerMonth },
    Source: 'Plan',
  };
}

/** Which window is stopping them — drives the message, since the reset differs. */
export type QuotaBlock = 'none' | 'daily' | 'monthly';

export function quotaBlock(quota: ImageQuota): QuotaBlock {
  if (quota.Source === 'OwnKey') return 'none';
  if (quota.Monthly.Remaining <= 0) return 'monthly';   // monthly checked first: it's the harder stop
  if (quota.Daily.Remaining <= 0) return 'daily';
  return 'none';
}

/**
 * One ready-made setting for a product shot.
 *
 * `id` is the SERVER's prompt id, posted back verbatim — the wording that actually reaches the
 * model lives in the API's prompt registry and is versioned there, because a prompt change is a
 * contract change and must not be something a client can make. What lives here is only what the
 * artist reads, which has to be translatable and therefore cannot come from the server.
 *
 * The two halves are kept honest by the server: it accepts only ids on its own whitelist, so a
 * setting removed or renamed in the API surfaces immediately as a refusal — before anything is
 * spent — rather than as a shot that quietly came back looking wrong.
 */
export interface SceneSetting {
  id: string;
  labelKey: string;
  descKey: string;
}

/** In the order artists see them. Mirrors `ImageGenerationClient.SceneSettingIds`. */
export const SCENE_SETTINGS: SceneSetting[] = [
  { id: 'ai-scene-wall-v1',   labelKey: 'imageStudio.sceneSettingWall',   descKey: 'imageStudio.sceneSettingWallDesc' },
  { id: 'ai-scene-plinth-v1', labelKey: 'imageStudio.sceneSettingPlinth', descKey: 'imageStudio.sceneSettingPlinthDesc' },
  { id: 'ai-scene-shelf-v1',  labelKey: 'imageStudio.sceneSettingShelf',  descKey: 'imageStudio.sceneSettingShelfDesc' },
  { id: 'ai-scene-table-v1',  labelKey: 'imageStudio.sceneSettingTable',  descKey: 'imageStudio.sceneSettingTableDesc' },
  { id: 'ai-scene-held-v1',   labelKey: 'imageStudio.sceneSettingHeld',   descKey: 'imageStudio.sceneSettingHeldDesc' },
];

/** Matches the server's cap. Longer text is trimmed there, so trim it here where it can be seen. */
export const SCENE_DETAIL_MAX = 300;

/**
 * What a generative call needs beyond its source. Catalogue sends none of it; a product shot sends
 * both, and the server treats a missing setting as its default rather than as an error.
 */
export interface GenerateOptions {
  /** A `SceneSetting.id`. */
  promptId?: string;
  /** The artist's own words about the SETTING. Never about the piece — the server ignores that. */
  sceneDescription?: string;
}

/**
 * True for an image whose frame shows more than the artwork — today, a product shot.
 *
 * Read from `Operation`, never from `Kind`: a catalogue image is `Generated` too and is a perfectly
 * good main image, because it is still a picture of the work alone. What separates them is what is
 * in the frame, and only the operation records that. Mirrors `ImagePipelineModel.IsMarketingAsset`;
 * the server enforces it either way, this is what stops the UI offering an action that would fail.
 */
export function isMarketingAsset(asset: ImageAsset): boolean {
  return (asset?.Operation || '').toLowerCase() === 'scene';
}

/**
 * `1.2.0` added `Geometry.Frame` (2026-08-26). Additive and optional — a `1.1.0` recipe renders
 * identically under it, and stored recipes are never migrated, only re-rendered as written.
 */
export const RECIPE_SCHEMA_VERSION = '1.2.0';

export const NEUTRAL_ADJUSTMENTS: RecipeAdjustments = {
  Exposure: 0, Brightness: 0, Contrast: 0, Highlights: 0, Shadows: 0,
  Temperature: 0, Tint: 0, Saturation: 0, Vibrance: 0, Denoise: 0,
  Sharpen: { Amount: 0, Radius: 1, Threshold: 0 },
};

export function neutralRecipe(sourceAssetId: string): EditRecipe {
  return {
    SchemaVersion: RECIPE_SCHEMA_VERSION,
    SourceAssetId: sourceAssetId,
    Geometry: { Rotate: 0 },
    Adjustments: { ...NEUTRAL_ADJUSTMENTS, Sharpen: { ...NEUTRAL_ADJUSTMENTS.Sharpen } },
    Presentation: null,
    Output: { ColourSpace: 'sRGB', Derivatives: ['master', 'catalogue', 'web', 'thumb'] },
  };
}

/** The whole frame — what `Crop` means when the artist has not reframed anything. */
export const FULL_FRAME: RecipeCrop = { X: 0, Y: 0, Width: 1, Height: 1 };

/**
 * True when a crop keeps everything. Compared with a tolerance rather than exactly: the framing
 * comes from pointer arithmetic, so "dragged out and back again" lands on 0.999998 rather than 1
 * and would otherwise submit a render that changes nothing.
 */
export function isFullFrame(crop: RecipeCrop | null | undefined): boolean {
  if (!crop) return true;
  return crop.X <= 0.001 && crop.Y <= 0.001 && crop.Width >= 0.999 && crop.Height >= 0.999;
}

/**
 * True when a frame changes nothing — the work still fills its canvas AND has not been moved.
 *
 * Both halves matter: a work at full scale that has been shifted is still reframed, and reading
 * only the scale would drop the artist's repositioning on the floor as "no change to render".
 */
export function isUnframed(frame: RecipeFrame | null | undefined): boolean {
  if (!frame) return true;
  return frame.Scale >= 0.999 && Math.abs(frame.X) <= 0.001 && Math.abs(frame.Y) <= 0.001;
}

/** True when the artist has neither zoomed into the work nor stood it back from its edges. */
export function isDefaultFraming(recipe: EditRecipe): boolean {
  return isFullFrame(recipe.Geometry.Crop) && isUnframed(recipe.Geometry.Frame);
}

/** True when nothing would change — used to avoid submitting a pointless render. */
export function isNeutralRecipe(recipe: EditRecipe): boolean {
  const a = recipe.Adjustments;
  return isDefaultFraming(recipe)
    && recipe.Geometry.Rotate === 0
    && a.Exposure === 0 && a.Brightness === 0 && a.Contrast === 0
    && a.Highlights === 0 && a.Shadows === 0 && a.Temperature === 0
    && a.Tint === 0 && a.Saturation === 0 && a.Vibrance === 0
    && a.Denoise === 0 && a.Sharpen.Amount === 0;
}
